import { supabase } from '@/lib/supabase'
import { normalizeError } from '@/features/gallery/api/errors'
import type {
  ProjectPage,
  ProjectPageFormData,
  ProjectPageQueryParams,
  ProjectPageListResponse,
  ProjectPageWithGallery
} from '@/types/projectPages'

/**
 * Project Pages API
 *
 * Data access layer for project_pages table operations.
 * Handles CRUD operations, filtering, pagination, and slug validation.
 *
 * Architecture: UI → Supabase (direct) → Database
 * Never fetches external APIs - all data comes from/goes to Supabase.
 *
 * @see /apps/fixmybrick-admin-dashboard/CLAUDE.md - Critical Data Flow Pattern
 */

/**
 * Fetch project pages with filtering, sorting, and pagination
 */
export async function fetchProjectPages(
  params: ProjectPageQueryParams
): Promise<ProjectPageListResponse> {
  try {
    const {
      page,
      pageSize,
      searchTerm,
      status = [],
      featured,
      service_city = [],
      service_province = [],
      service_type = [],
      tags = [],
      date_from,
      date_to,
      publish_date_from,
      publish_date_to,
      sortBy = 'created_at',
      sortDir = 'desc'
    } = params

    const from = (page - 1) * pageSize
    const to = from + pageSize - 1

    let query = supabase
      .from('project_pages')
      .select('*, gallery:galleries(id, title, cover_image, franchise_region)', { count: 'estimated' })
      .order(sortBy, { ascending: sortDir === 'asc' })

    // Search
    const term = (searchTerm || '').trim()
    if (term) {
      query = query.or(`title.ilike.%${term}%,description.ilike.%${term}%,slug.ilike.%${term}%`)
    }

    // Filters
    if (status.length > 0) query = query.in('status', status)
    if (featured !== undefined) query = query.eq('featured', featured)
    if (service_city.length > 0) query = query.in('service_city', service_city)
    if (service_province.length > 0) query = query.in('service_province', service_province)
    if (service_type.length > 0) query = query.in('service_type', service_type)
    if (tags.length > 0) query = query.contains('tags', tags)

    // Date ranges
    if (date_from) query = query.gte('created_at', date_from)
    if (date_to) query = query.lte('created_at', date_to)
    if (publish_date_from) query = query.gte('publish_at', publish_date_from)
    if (publish_date_to) query = query.lte('publish_at', publish_date_to)

    const { data, error, count } = await query.range(from, to)
    if (error) throw error

    return {
      pages: data || [],
      totalCount: count || 0,
      page,
      pageSize
    }
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch project pages')
  }
}

/**
 * Fetch a single project page by ID with gallery details
 */
export async function fetchProjectPageById(id: string): Promise<ProjectPageWithGallery> {
  try {
    const { data, error } = await supabase
      .from('project_pages')
      .select('*, gallery:galleries(id, title, cover_image, franchise_region)')
      .eq('id', id)
      .single()

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch project page')
  }
}

/**
 * Fetch a single project page by slug (for public viewing)
 */
export async function fetchProjectPageBySlug(slug: string): Promise<ProjectPageWithGallery> {
  try {
    const { data, error } = await supabase
      .from('project_pages')
      .select('*, gallery:galleries(id, title, cover_image, franchise_region)')
      .eq('slug', slug)
      .single()

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch project page by slug')
  }
}

/**
 * Fetch a project page by gallery ID (if one exists)
 * Returns null if no project page exists for this gallery
 *
 * @see GalleryJobPage.tsx - Used to show "View Project Page" vs "Create Project Page" button
 */
export async function fetchProjectPageByGalleryId(
  galleryId: number
): Promise<ProjectPageWithGallery | null> {
  try {
    const { data, error } = await supabase
      .from('project_pages')
      .select('*, gallery:galleries(id, title, cover_image, franchise_region)')
      .eq('gallery_id', galleryId)
      .maybeSingle() // Returns null if no match, vs single() which throws

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to fetch project page by gallery')
  }
}

/**
 * Create a new project page
 */
export async function createProjectPage(
  formData: ProjectPageFormData
): Promise<ProjectPage> {
  try {
    // Get the current authenticated user
    const { data: { user } } = await supabase.auth.getUser()

    // Add created_by and updated_by fields
    const dataToInsert = {
      ...formData,
      created_by: user?.id,
      updated_by: user?.id
    }

    const { data, error } = await supabase
      .from('project_pages')
      .insert(dataToInsert)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to create project page')
  }
}

/**
 * Update an existing project page
 */
export async function updateProjectPage(
  id: string,
  updates: Partial<ProjectPageFormData>
): Promise<ProjectPage> {
  try {
    // Get the current authenticated user
    const { data: { user } } = await supabase.auth.getUser()

    // Add updated_by field
    const dataToUpdate = {
      ...updates,
      updated_by: user?.id
    }

    const { data, error } = await supabase
      .from('project_pages')
      .update(dataToUpdate)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to update project page')
  }
}

/**
 * Delete a project page
 */
export async function deleteProjectPage(id: string): Promise<void> {
  try {
    const { error } = await supabase.from('project_pages').delete().eq('id', id)
    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to delete project page')
  }
}

/**
 * Check if a slug is unique (excluding a specific ID for updates)
 */
export async function checkSlugUnique(
  slug: string,
  excludeId?: string
): Promise<{ isUnique: boolean; existingId?: string }> {
  try {
    let query = supabase.from('project_pages').select('id').eq('slug', slug)

    if (excludeId) {
      query = query.neq('id', excludeId)
    }

    const { data, error } = await query.limit(1).single()

    if (error) {
      // PGRST116 means no rows found (slug is unique)
      if (error.code === 'PGRST116') {
        return { isUnique: true }
      }
      throw error
    }

    return { isUnique: false, existingId: data?.id }
  } catch (err) {
    throw normalizeError(err, 'Failed to check slug uniqueness')
  }
}

/**
 * Generate a unique slug from a title
 */
export async function generateUniqueSlug(baseSlug: string): Promise<string> {
  let slug = baseSlug
  let counter = 1

  while (true) {
    const { isUnique } = await checkSlugUnique(slug)
    if (isUnique) return slug

    slug = `${baseSlug}-${counter}`
    counter++

    // Safety: prevent infinite loops
    if (counter > 100) {
      throw new Error('Could not generate unique slug after 100 attempts')
    }
  }
}

/**
 * Publish a project page immediately or schedule for later
 */
export async function publishProjectPage(
  id: string,
  publishAt?: string
): Promise<ProjectPage> {
  try {
    const updates: Partial<ProjectPage> = {
      status: 'published',
      publish_at: publishAt || new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('project_pages')
      .update(updates)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to publish project page')
  }
}

/**
 * Unpublish a project page (set to draft)
 */
export async function unpublishProjectPage(id: string): Promise<ProjectPage> {
  try {
    const { data, error } = await supabase
      .from('project_pages')
      .update({ status: 'draft', publish_at: null })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to unpublish project page')
  }
}

/**
 * Archive a project page
 */
export async function archiveProjectPage(id: string): Promise<ProjectPage> {
  try {
    const { data, error } = await supabase
      .from('project_pages')
      .update({ status: 'archived' })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to archive project page')
  }
}

/**
 * Toggle featured status
 */
export async function toggleFeatured(id: string, featured: boolean): Promise<ProjectPage> {
  try {
    const { data, error } = await supabase
      .from('project_pages')
      .update({ featured })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data
  } catch (err) {
    throw normalizeError(err, 'Failed to toggle featured status')
  }
}

/**
 * Bulk update project pages
 */
export async function bulkUpdateProjectPages(
  ids: string[],
  updates: Partial<ProjectPage>
): Promise<void> {
  if (!ids || ids.length === 0) return

  try {
    const { error } = await supabase
      .from('project_pages')
      .update(updates)
      .in('id', ids)

    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to bulk update project pages')
  }
}

/**
 * Bulk delete project pages
 */
export async function bulkDeleteProjectPages(ids: string[]): Promise<void> {
  if (!ids || ids.length === 0) return

  try {
    const { error } = await supabase
      .from('project_pages')
      .delete()
      .in('id', ids)

    if (error) throw error
  } catch (err) {
    throw normalizeError(err, 'Failed to bulk delete project pages')
  }
}

/**
 * Get publishing info for a project page
 */
export function getPublishingInfo(page: ProjectPage) {
  const now = new Date()
  const publishAt = page.publish_at ? new Date(page.publish_at) : null

  return {
    isDraft: page.status === 'draft',
    isPublished: page.status === 'published' && (!publishAt || publishAt <= now),
    isScheduled: page.status === 'published' && publishAt !== null && publishAt > now,
    isArchived: page.status === 'archived',
    canPublish: page.status !== 'published',
    scheduledFor: publishAt && publishAt > now ? page.publish_at : undefined,
    publishedAt: publishAt && publishAt <= now ? page.publish_at : undefined
  }
}
